/*
 * The i18n layer.
 *
 * Arch §6 is unusually direct about why this exists in v1 despite shipping one language: "Global
 * launch is a stated goal, so i18n is a v1 constraint even though v1 ships in one language.
 * Retrofitting it is a full-application refactor."
 *
 * Four of §6.1's layers live here:
 *
 *   UI strings   ICU message format, externalised, no concatenated sentences. Concatenation is
 *                called out separately because it is the one that survives a naive extraction —
 *                `t('greeting') + name` looks externalised and is not translatable.
 *   Locale model "Locale is a user attribute, not a browser guess." resolveLocale enforces the
 *                order; nothing here reads navigator.language.
 *   Formatting   every date, number and currency through Intl. Never string-built.
 *   Content data not here — that is a locale column on tenant-authored rows, and it belongs to
 *                whichever module owns the table.
 *
 * ICU parsing is `intl-messageformat` rather than hand-rolled. Plural categories are not a
 * formatting nicety — Hindi, Polish and Arabic disagree about how many there are, and a homegrown
 * `count === 1 ? a : b` is a bug that only appears in the language nobody on the team reads.
 */

import { IntlMessageFormat } from 'intl-messageformat';
import source from './messages/en-IN.json' with { type: 'json' };

/**
 * The source catalogue doubles as the key type, so `t('does.not.exist')` is a compile error
 * rather than a string rendered as itself at runtime.
 */
export type MessageKey = keyof typeof source;
export type Messages = Record<MessageKey, string>;

export type MessageValues = Record<string, string | number | boolean | Date | null | undefined>;

/** The only locale with a catalogue today. Adding one means adding a file and a case here. */
export const SOURCE_LOCALE = 'en-IN';

export const CATALOGUES: Readonly<Record<string, Messages>> = {
  [SOURCE_LOCALE]: source as Messages,
};

export const SUPPORTED_LOCALES = Object.keys(CATALOGUES);

export interface LocalePreferences {
  /** The user's own setting. Wins, per arch §6.1 — locale is a user attribute. */
  readonly user?: string | null;
  /** The tenant default, applied to a user who has expressed no preference. */
  readonly tenant?: string | null;
}

/**
 * Resolves the locale to render in.
 *
 * Deliberately does not consult the browser. A recruiter working on a colleague's machine, or on
 * a laptop bought abroad, gets their own locale — and more importantly, the locale a screen
 * rendered in becomes reproducible from data rather than from where the request came from.
 */
export function resolveLocale(preferences: LocalePreferences): string {
  for (const candidate of [preferences.user, preferences.tenant]) {
    if (candidate && SUPPORTED_LOCALES.includes(candidate)) {
      return candidate;
    }
  }
  return SOURCE_LOCALE;
}

export interface Translator {
  readonly locale: string;
  /** Formats a catalogue message. Values are ICU arguments, not string concatenation. */
  t(key: MessageKey, values?: MessageValues): string;
  formatNumber(value: number, options?: Intl.NumberFormatOptions): string;
  /** Money is always an amount *and* a currency — entity model §3 makes that a schema rule. */
  formatCurrency(amount: number, currency: string): string;
  /**
   * The same thing, for money as it actually arrives — a decimal **string**.
   *
   * ── Why this is the one screens should call ────────────────────────────────────────────────────
   *
   * DB-04 keeps money in `numeric` with its currency beside it, and node-postgres hands a `numeric`
   * back as a string so no float ever touches it. Every screen therefore holds `"1800000"`, and
   * every screen was interpolating that straight into a message: `{currency} {value}` produced
   * **"INR 1800000"**. Nobody writes a salary that way in any market, and in this one the grouping
   * is not even the same shape — ₹18,00,000 is lakhs, not millions.
   *
   * `Intl` has known both facts all along; nothing was reaching it, because the only formatter took
   * a `number` and the values were strings. This takes the string, so there is no reason left not
   * to use it.
   *
   * A blank or unparseable amount returns `null` rather than "NaN" or "₹0" — an absent figure and a
   * figure of zero are different claims, and a screen showing "₹0.00" for "nobody said" is lying.
   */
  money(amount: string | null | undefined, currency: string | null | undefined): string | null;
  formatDate(value: Date | string, options?: Intl.DateTimeFormatOptions): string;
  formatDateTime(value: Date | string): string;
}

/**
 * The currencies a figure may be quoted in.
 *
 * Short and deliberately so: an agency in this market bills in rupees, and in dollars, dirhams,
 * pounds or euros when the client is abroad. A full ISO 4217 list would be a menu of a hundred and
 * eighty entries where the answer is nearly always the first one.
 *
 * Here rather than in a screen because two screens already disagreed about it — one offered a
 * free-text box of three characters, which accepted `xxx`, `Rs.` and `inr` alike.
 */
export const CURRENCIES = ['INR', 'USD', 'EUR', 'GBP', 'AED', 'SGD', 'AUD', 'CAD'] as const;

export type CurrencyCode = (typeof CURRENCIES)[number];

/**
 * The glyph for a currency — ₹, $, € — for menus and labels where the code alone reads as a
 * database value rather than as money.
 *
 * Derived from `Intl` rather than kept as a table, so it cannot drift and so it is right in every
 * locale this ever runs in. `formatToParts` is the only way to ask for the symbol on its own; the
 * fallback is the code itself, which is honest if unlovely.
 */
export function symbolFor(currency: string, locale = SOURCE_LOCALE): string {
  try {
    const parts = new Intl.NumberFormat(locale, {
      style: 'currency',
      currency,
      currencyDisplay: 'narrowSymbol',
    }).formatToParts(0);
    return parts.find((part) => part.type === 'currency')?.value ?? currency;
  } catch {
    return currency;
  }
}

const toDate = (value: Date | string): Date =>
  value instanceof Date ? value : new Date(value);

export function createTranslator(locale: string = SOURCE_LOCALE): Translator {
  const messages = CATALOGUES[locale] ?? CATALOGUES[SOURCE_LOCALE]!;

  // Compiled messages and Intl formatters are both expensive to construct and cheap to keep.
  // Cached per translator rather than globally, so a locale change drops the whole set.
  const compiled = new Map<string, IntlMessageFormat>();
  const numberFormats = new Map<string, Intl.NumberFormat>();
  const dateFormats = new Map<string, Intl.DateTimeFormat>();

  const numberFormat = (options: Intl.NumberFormatOptions) => {
    const cacheKey = JSON.stringify(options);
    let format = numberFormats.get(cacheKey);
    if (!format) {
      format = new Intl.NumberFormat(locale, options);
      numberFormats.set(cacheKey, format);
    }
    return format;
  };

  const dateFormat = (options: Intl.DateTimeFormatOptions) => {
    const cacheKey = JSON.stringify(options);
    let format = dateFormats.get(cacheKey);
    if (!format) {
      format = new Intl.DateTimeFormat(locale, options);
      dateFormats.set(cacheKey, format);
    }
    return format;
  };

  return {
    locale,

    t(key, values) {
      const pattern = messages[key];
      if (pattern === undefined) {
        // Returning the key is the least-bad failure: it is obviously wrong on screen, greppable,
        // and it does not throw in a render path. The lint and the catalogue test are what stop
        // it reaching a user; this is the runtime backstop.
        return key;
      }

      let message = compiled.get(key);
      if (!message) {
        message = new IntlMessageFormat(pattern, locale);
        compiled.set(key, message);
      }

      return String(message.format(values as never));
    },

    formatNumber(value, options = {}) {
      return numberFormat(options).format(value);
    },

    formatCurrency(amount, currency) {
      return numberFormat({ style: 'currency', currency }).format(amount);
    },

    money(amount, currency) {
      if (amount === null || amount === undefined || String(amount).trim() === '') return null;
      const parsed = Number(amount);
      if (!Number.isFinite(parsed)) return null;

      /*
       * `narrowSymbol`, so INR renders as ₹ rather than "₹" in some locales and "INR" in others,
       * and USD as $ rather than US$. The fallback is the plain style: a currency `Intl` does not
       * know throws rather than degrading, and a screen is not the place to find that out.
       */
      try {
        return numberFormat({
          style: 'currency',
          currency: currency ?? 'INR',
          currencyDisplay: 'narrowSymbol',
          // Whole rupees. A salary band is not quoted to the paisa, and two trailing zeros on every
          // figure is noise on a screen that shows several at once.
          maximumFractionDigits: 2,
          minimumFractionDigits: 0,
        }).format(parsed);
      } catch {
        return `${currency ?? ''} ${numberFormat({}).format(parsed)}`.trim();
      }
    },

    formatDate(value, options = { dateStyle: 'medium' }) {
      return dateFormat(options).format(toDate(value));
    },

    formatDateTime(value) {
      return dateFormat({ dateStyle: 'medium', timeStyle: 'short' }).format(toDate(value));
    },
  };
}
